import layout from './AutoGeneratingSummaryView.html?raw';
import AsyncSummaryView from '@/SummaryViews/AsyncSummaryView';
import Factory from '@/Factories/Factory';
import ComponentManager from '@/Management/ComponentManager';
import WindowType from '@/Controls/Window';
import Loader from '@/Utility/Loader';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import AutoGeneratingSummaryViewModel from '@/ViewModels/SummaryViews/AutoGeneratingSummaryViewModel';
import PropertyGroupFactory from '@/Factories/GroupFactories/PropertyGroupFactory';
import { IDetailViewDTO } from "coreframetypings/dto";

export default class AutoGeneratingSummaryView extends AsyncSummaryView {

    private Window = ComponentManager.getControl<typeof WindowType>('Window');

    public factory1: PropertyGroupFactory;
    public factory2: PropertyGroupFactory;
    public summaryView: JQuery<HTMLElement>;
    public window: WindowType;

    constructor(protected viewModel: AutoGeneratingSummaryViewModel, private customLayout?: string) {
        super();

        this.factory1 = null;
        this.factory2 = null;
    }


    public async getElementAsync(): Promise<JQuery> {
        if (this.summaryView != null) {
            return this.summaryView;
        }

        this.viewModel.increaseBusyCounter();

        this.summaryView = this.applyViewModel(this.customLayout || layout, this.viewModel);

        const showLoader = this.defineComputed(() => {
            const vmBusy = this.viewModel.isBusy();
            const windowOpen = this.viewModel.window?.isOpen();
            return vmBusy && !windowOpen;
        });

        this.applyBindings(this.summaryView, {
            controlName: 'autogeneratingsummaryview',
            loader: showLoader
        });

        if (this.viewModel.openFullScreenSummaryView) {
            this.viewModel.link.onClick(false);
        }

        this.window = this.createWindow();

        const renderTrigger = this.defineComputed(() => {
            const displayContent = this.viewModel.displayContent()
            const detailViewContext = this.viewModel.detailViewContext();
            const detailView = detailViewContext?.detailView();
            return {
                displayContent,
                detailViewContext,
                detailView,
            };
        });

        await new Promise<void>((resolve) => {
            this.subscribeUntil(renderTrigger, async ({ displayContent, detailViewContext, detailView }) => {
                const rendered = await this.tryRenderAsync(displayContent, detailViewContext, detailView);
                if (rendered) {
                    resolve();
                }
                return rendered;
            });
        });

        if (this.isDisposed || this.viewModel.isDisposed)
            return null;

        this.viewModel.decreaseBusyCounter();

        this.defineSubscription(renderTrigger, async ({ displayContent, detailViewContext, detailView }) => {
            try {
                this.viewModel.increaseBusyCounter();
                await this.tryRenderAsync(displayContent, detailViewContext, detailView);
            }
            finally {
                this.viewModel.decreaseBusyCounter();
            }
        });

        return this.summaryView;
    }

    protected async tryRenderAsync(displayContent: boolean, detailViewContext: DetailViewContext, detailView: IDetailViewDTO) {
        if (this.factory1?.isDisposed === false) {
            this.factory1.dispose();
        }

        if (!displayContent)
            return true;

        if (!detailViewContext || !detailView)
            return false;

        const $summaryViewContent = this.summaryView.find('.content');
        this.factory1 = Factory.createInstance<PropertyGroupFactory>('PropertyGroup', {
            detailViewGroup: detailView,
            context: detailViewContext,
            layout: 'NoneLayout',
            isSummaryViewMode: true,
            locator: detailViewContext.locator // Der DVC wurde bereits für diese SV angelegt und hat einen passenden Locator
        });

        const tuple = await this.factory1.getTupleAsync();
        if (this.isDisposed || detailViewContext.isDisposed)
            return true;

        detailViewContext.setCreatedTuple('$rootgroup', tuple);
        $summaryViewContent.empty().append(tuple.control);

        return true;
    }

    private createWindow() {
        if (!this.viewModel.window)
            return null;

        const window = new this.Window(this.viewModel.window);
        let tuple = window.getTuple();

        tuple.control.appendTo(this.summaryView);

        this.defineComputed(async () => {
            const windowContent = this.viewModel.window.content();
            const isOpen = this.viewModel.window.isOpen();
            const fullScreenContext = this.viewModel.fullScreenContext();

            if (!isOpen || !fullScreenContext) {
                if (this.factory2 && !this.factory2.isDisposed) {
                    this.factory2.dispose();
                }

                if (fullScreenContext && !fullScreenContext.isDisposed) {
                    fullScreenContext.dispose();
                }
                return;
            }

            if (fullScreenContext.isInit()) {
                const detailView = fullScreenContext.detailView();

                if (detailView != null && windowContent.length > 0) {
                    // onBeforeRender (summaryView) not neccessary, because only dto changes are possible
                    // and are here still present, ViewModels are not yet created
                    Loader.getInstance().show();
                    await ko.ignoreDependencies(async () => {
                        this.factory2 = Factory.createInstance<PropertyGroupFactory>('PropertyGroup', {
                            detailViewGroup: detailView,
                            context: fullScreenContext,
                            layout: 'NoneLayout',
                            isSummaryViewMode: true,
                            locator: this.defineComputed(() => {
                                return fullScreenContext.locator() + '.fullscreen';
                            })
                        });

                        const tuple = await this.factory2.getTupleAsync();
                        windowContent.empty().append(tuple.control);
                    });
                    // fire summaryViewReady (NEW ViewModels)
                    if (!fullScreenContext.isDisposed)
                        fullScreenContext.notifySummaryViewReady();
                    Loader.getInstance().hide();
                }
            }
        });

        return window;
    }

    public getScrollPosition(): number {
        if (this.isDisposed || !this.summaryView)
            return 0;

        return this.summaryView.find('.content').scrollTop();
    }

    public scrollTo(position: number): void {
        if (this.isDisposed || !this.summaryView)
            return;

        this.summaryView.find('.content').scrollTop(position);
    }
}

ComponentManager.setSummaryView('AutoGeneratingSummaryView', AutoGeneratingSummaryView, 'RADPresentation.HTMLClient.DefaultViewExtensions');